import { StorageConstants } from "@/constants";
import { useEffect, useState } from "react";

import { type FileTextState } from "./types";

export function useFileText(fileUrl: string): FileTextState {
  const [text, setText] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setText(null);
    setIsLoading(true);
    setHasLoadError(false);
    setIsTruncated(false);

    const loadText = async (): Promise<void> => {
      try {
        const response = await fetch(fileUrl);
        // An error page body is valid text, so it would otherwise render as the file's content.
        if (!response.ok) throw new Error(`Text file fetch failed: ${response.status}`);
        const body = await response.text();
        if (cancelled) return;
        const exceedsPreviewLimit = body.length > StorageConstants.MAX_TEXT_PREVIEW_CHARACTERS;
        setText(
          exceedsPreviewLimit ? body.slice(0, StorageConstants.MAX_TEXT_PREVIEW_CHARACTERS) : body
        );
        setIsTruncated(exceedsPreviewLimit);
      } catch (error) {
        console.error("[TextViewer] Error loading text file:", fileUrl, error);
        if (!cancelled) setHasLoadError(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadText();
    return () => {
      cancelled = true;
    };
  }, [fileUrl]);

  return { text, isLoading, hasLoadError, isTruncated };
}
